import { RefObject, useLayoutEffect, useRef } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import { WEB_FONT_FAMILIES } from "src/domains/shared/constants/styles/typography/webTypography";
import {
  TOOL_GUIDE_ACTION_KEYS,
  TOOL_GUIDE_BUTTON_KEYS,
  TOOL_GUIDE_PRECISE_PENDING_KEY,
  ToolGuideAction,
  ToolGuideReadout,
  ToolGuideRow,
} from "src/domains/shared/utils/toolGuideMessage";
import { MeasurementAxisIcon } from "./MeasurementAxisIcon";
import {
  CursorPosition,
  useContainerCursorPosition,
} from "./useContainerCursorPosition";

const CURSOR_OFFSET_X = 14;
const CURSOR_OFFSET_Y = 14;
const CHIP_Z_INDEX = 100;
const LOADING_SPINNER_SIZE = 10;
const LOADING_SPINNER_THICKNESS = 5.5;

export interface ToolGuideChipProps {
  containerRef: RefObject<HTMLElement | null>;
  /** 버튼별 한 줄. `null`·빈 배열이고 `loading` 이 아니면 렌더하지 않는다 */
  rows: ToolGuideRow[] | null;
  /** 3D 정밀 생성 대기 변형 — 줄 목록 대신 스피너 한 줄 */
  loading?: boolean;
  /**
   * 측정 도구의 실시간 값 — 안내 칩과 분리된 값 칩(커서 오른쪽 아래, 안내 칩과 상하 대칭). 값은 React 가 아니라
   * readout 이 `bind` 로 받은 DOM 에 직접 쓴다(이동 빈도 경로). `null` 이면 값 칩 없음
   */
  readout?: ToolGuideReadout | null;
}

/** readout 이 값 span 을 찾는 표식 — `measurementReadout` 과 같은 이름 */
export const TOOL_GUIDE_READOUT_VALUE_ATTR = "data-readout-value";

export function ToolGuideChip({
  containerRef,
  rows,
  loading = false,
  readout = null,
}: ToolGuideChipProps) {
  const { t } = useTranslation("common");
  const chipRef = useRef<HTMLDivElement | null>(null);
  const lastPositionRef = useRef<CursorPosition | null>(null);

  function placeChipAt({ x, y }: CursorPosition) {
    const chip = chipRef.current;
    if (!chip) return;
    chip.style.transform = `translate3d(${x + CURSOR_OFFSET_X}px, ${y - CURSOR_OFFSET_Y}px, 0) translateY(-100%)`;
    chip.style.visibility = "visible";
  }

  function placeChipAtCursor(position: CursorPosition) {
    lastPositionRef.current = position;
    placeChipAt(position);
  }

  const { inside } = useContainerCursorPosition(containerRef, placeChipAtCursor);

  const hasRows = rows !== null && rows.length > 0;
  const visible = inside && (hasRows || loading);

  useLayoutEffect(() => {
    if (!visible || !lastPositionRef.current) return;
    placeChipAt(lastPositionRef.current);
  }, [visible]);

  if (!visible) return null;

  return (
    <Box ref={chipRef} role="status" aria-live="polite" sx={overlay}>
      {loading ? (
        <Box sx={[chipSurface, loadingSurface]}>
          <CircularProgress
            size={LOADING_SPINNER_SIZE}
            thickness={LOADING_SPINNER_THICKNESS}
            sx={loadingSpinner}
          />
          <Box component="span" sx={lineText}>
            {t(TOOL_GUIDE_PRECISE_PENDING_KEY)}
          </Box>
        </Box>
      ) : (
        <Box sx={[chipSurface, rowsSurface]}>
          {rows?.map(({ button, action }) => (
            <Box key={button} component="span" sx={rowLine}>
              <Box component="span" sx={buttonLabel}>
                {t(TOOL_GUIDE_BUTTON_KEYS[button])}
              </Box>
              <Box component="span" sx={ACTION_LABEL_STYLE[action]}>
                {t(TOOL_GUIDE_ACTION_KEYS[action])}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {/* 로딩 분기 밖 — 클릭 정밀 픽 대기로 안내 칩이 로딩 변형이 돼도 값 칩은 그대로 남는다(깜빡임 방지) */}
      {readout && (
        <Box
          ref={(element: HTMLDivElement | null) => readout.bind("container", element)}
          sx={[chipSurface, readoutChip]}
          data-testid="tool-guide-readout"
          // 칩 루트는 polite 라이브 영역 — 매 프레임 바뀌는 값까지 읽히지 않도록 값 칩은 라이브 알림에서 뺀다
          aria-live="off"
        >
          {readout.rows.map(({ axis, labelKey, color }) => (
            <Box
              key={axis}
              ref={(element: HTMLDivElement | null) => readout.bind(axis, element)}
              sx={readoutRow}
              data-readout-axis={axis}
            >
              <MeasurementAxisIcon axis={axis} color={color} />
              <Box component="span" sx={readoutAxisName} style={{ color }}>
                {t(labelKey)}
              </Box>
              <Box
                component="span"
                sx={readoutValue}
                {...{ [TOOL_GUIDE_READOUT_VALUE_ATTR]: "" }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

const overlay = {
  position: "fixed",
  top: 0,
  left: 0,
  visibility: "hidden",
  willChange: "transform",
  pointerEvents: "none",
  zIndex: CHIP_Z_INDEX,
} as const;

/**
 * 흰 표면 키캡 스타일 — 사이드 패널과 같은 밝은 톤. 너비는 내용에 맞추고(nowrap·inline),
 * 여백은 줄이 답답하지 않을 만큼만. 지도 위 경계는 옅은 테두리 + 그림자가 잡는다.
 */
const chipSurface = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "8px 12px 8px 10px",
  borderRadius: "10px",
  backgroundColor: COLOR_CONSTANTS.common.white_states.main,
  border: `1px solid ${COLOR_CONSTANTS.divider}`,
  boxShadow: "0 6px 18px rgba(0, 0, 0, 0.22)",
  color: COLOR_CONSTANTS.text.primary,
  fontFamily: WEB_FONT_FAMILIES.primary,
  fontSize: "12.5px",
  fontWeight: 600,
  whiteSpace: "nowrap",
} as const;

/**
 * 실시간 값 칩 — 커서 오른쪽 아래, 오른쪽 위의 안내 칩과 커서 가로선 기준 상하 대칭.
 * 오버레이(= 안내 칩 상자)의 아래 가장자리가 커서 −`CURSOR_OFFSET_Y` 이므로 그보다 2 × 오프셋 아래가
 * 커서 +`CURSOR_OFFSET_Y` 다. 위치는 오버레이 transform 하나로 함께 움직인다.
 * 보이는 값이 없으면 readout 이 이 칩을 통째로 숨긴다.
 */
const readoutChip = {
  position: "absolute",
  left: 0,
  top: `calc(100% + ${CURSOR_OFFSET_Y * 2}px)`,
  // 절대 위치 자식은 포함 상자(안내 칩) 너비로 줄어들 수 있다 — 내용 너비로 고정
  width: "max-content",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "3px",
} as const;

const readoutRow = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
} as const;

const readoutAxisName = {
  fontSize: "9px",
  fontWeight: 700,
  lineHeight: "12px",
} as const;

const readoutValue = {
  fontSize: "11px",
  fontWeight: 600,
  lineHeight: "12px",
  color: COLOR_CONSTANTS.text.primary,
  fontVariantNumeric: "tabular-nums",
} as const;

/** 한 줄 = 버튼 하나. 키캡 열은 가장 긴 라벨에 맞춰 정렬된다 */
const rowsSurface = {
  display: "grid",
  gridTemplateColumns: "auto auto",
  alignItems: "center",
  columnGap: "9px",
  rowGap: "6px",
} as const;

const rowLine = {
  display: "contents",
} as const;

/** 세로 중앙 보정은 정수 px 만 — 소수 px 는 Windows DPR 1·1.25·1.5 반올림 차이로 텍스트가 아래로 꺼진다 */
const lineText = {
  display: "inline-block",
  lineHeight: "16px",
} as const;

/** 키캡(kbd) — "누르는 것" 의 관습. 결과보다 한 단계 낮은 위계 */
const buttonLabel = {
  display: "inline-block",
  lineHeight: "15px",
  padding: "1px 7px",
  borderRadius: "5px",
  backgroundColor: COLOR_CONSTANTS.COLOR.GREY[100],
  border: `1px solid ${COLOR_CONSTANTS.COLOR.GREY[300]}`,
  borderBottomWidth: "2px",
  color: COLOR_CONSTANTS.text.secondary,
  fontSize: "11px",
  fontWeight: 600,
  textAlign: "center",
} as const;

const actionLabel = {
  ...lineText,
  color: COLOR_CONSTANTS.text.primary,
} as const;

/** 취소·되돌리기는 주 동작(점 생성·완료)보다 한 단계 낮춘다 */
const secondaryActionLabel = {
  ...actionLabel,
  color: COLOR_CONSTANTS.text.secondary,
  fontWeight: 500,
} as const;

/** 한 칩에 강조는 "완료" 하나 */
const finishActionLabel = {
  ...actionLabel,
  color: COLOR_CONSTANTS.primary.main,
} as const;

const ACTION_LABEL_STYLE: Record<ToolGuideAction, typeof actionLabel> = {
  createPoint: actionLabel,
  appendPoint: actionLabel,
  removeMeasurement: actionLabel,
  nearbyImage: actionLabel,
  // 놓으면 바로 생성돼 끝나는 동작 — 완료와 같은 강조
  placeEquipment: finishActionLabel,
  finish: finishActionLabel,
  cancel: secondaryActionLabel,
  undo: secondaryActionLabel,
  movePoint: actionLabel,
  removePoint: actionLabel,
};

/** 3D 정밀 생성 대기 — 주황 표면·글자로 대기 중임을 구분 */
const loadingSurface = {
  backgroundColor: COLOR_CONSTANTS.COLOR.ORANGE[50],
  border: `1px solid ${COLOR_CONSTANTS.warning._states.outlinedBorder}`,
  color: COLOR_CONSTANTS.warning.dark,
  fontWeight: 700,
} as const;

const loadingSpinner = {
  flex: "0 0 auto",
  color: COLOR_CONSTANTS.warning.main,
} as const;
